import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Profile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone_number?: string;
  department?: string;
  job_title?: string;
  onboarding_completed: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  gdpr_consent_at?: string;
  active_team_id?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
}

interface UserRole {
  id: string;
  role: 'super_admin' | 'admin' | 'manager' | 'user' | 'guest' | 'enterprise_admin' | 'enterprise_user';
  granted_at: string;
  expires_at?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  isSigningUp: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isEnterpriseAdmin: () => boolean;
  isOrganizationAdmin: (organizationId: string) => Promise<boolean>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the context for direct usage when needed
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSigningUp, setIsSigningUp] = useState(false); // Track if user is in signup process

  // Request deduplication and caching
  const fetchingRef = useRef(false);
  const cacheRef = useRef<{ userId: string; profile: Profile | null; roles: UserRole[]; timestamp: number } | null>(null);
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const fetchProfile = async (userId: string, isLogin = false) => {
    // Deduplication: Skip if already fetching
    if (fetchingRef.current) {
      // TODO: GA4_TRACKING - Already fetching profile, skipping
      return;
    }

    // Cache check: Use cached data if fresh (< 5 minutes old)
    if (
      cacheRef.current &&
      cacheRef.current.userId === userId &&
      Date.now() - cacheRef.current.timestamp < CACHE_TTL
    ) {
      // TODO: GA4_TRACKING - Using cached profile
      setProfile(cacheRef.current.profile);
      setRoles(cacheRef.current.roles);
      return;
    }

    // TODO: GA4_TRACKING - Fetching profile from database for user (userId)
    fetchingRef.current = true;

    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;

      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;

      // TODO: GA4_TRACKING - Profile fetched (found/not found)
      setProfile(profileData);
      setRoles(rolesData || []);

      // Cache the results
      cacheRef.current = {
        userId,
        profile: profileData,
        roles: rolesData || [],
        timestamp: Date.now()
      };

      // Only log audit and update last_login on actual login events
      if (isLogin) {
        // Log audit event for login
        await supabase.rpc('log_audit_event', {
          p_event_type: 'user_login',
          p_entity_type: 'auth',
          p_entity_id: userId,
          p_details: null
        });

        // Update last login
        await supabase
          .from('profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', userId);
      }

    } catch (error) {
      console.error('[AuthContext] Error fetching profile:', error);
    } finally {
      fetchingRef.current = false;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      // Invalidate cache on manual refresh
      cacheRef.current = null;
      await fetchProfile(user.id, false);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // TODO: GA4_TRACKING - Auth state change (event, userId)
        setSession(session);
        setUser(session?.user ?? null);

        // Fetch profile for any event where user exists (including INITIAL_SESSION on reload)
        if (session?.user) {
          const isLogin = event === 'SIGNED_IN';
          // TODO: GA4_TRACKING - Fetching profile for user (userId)
          setTimeout(() => {
            fetchProfile(session.user.id, isLogin);
          }, 0);
        } else if (!session) {
          // TODO: GA4_TRACKING - No session, clearing profile
          setProfile(null);
          setRoles([]);
          cacheRef.current = null;
        }

        setLoading(false);
      }
    );

    // Check for existing session (no duplicate fetch needed - onAuthStateChange handles it)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        toast.error(error.message);
      }

      return { error };
    } catch (error) {
      toast.error('An unexpected error occurred');
      return { error };
    }
  };

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    try {
      setIsSigningUp(true); // Mark that we're in signup process
      const redirectUrl = `${window.location.origin}/`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: metadata
        }
      });

      if (error) {
        toast.error(error.message);
        setIsSigningUp(false);
        return { error };
      }

      // Profile, team, membership, and role are now created automatically by database trigger
      // (see migration: 20260206000000_handle_new_user_trigger.sql)
      if (data.user) {
        // TODO: GA4_TRACKING - User created (userId)

        // Wait for database trigger to complete (profile creation, team setup, etc.)
        await new Promise(resolve => setTimeout(resolve, 500));

        // If we have a session (email verification disabled), fetch the profile
        if (data.session) {
          // TODO: GA4_TRACKING - Session available, fetching profile after signup
          cacheRef.current = null;
          await fetchProfile(data.user.id, false);
          // TODO: GA4_TRACKING - Profile fetched after signup
          toast.success('Account created successfully!');
        } else {
          // No session means email confirmation is required
          // TODO: GA4_TRACKING - No session, email confirmation required
          toast.success('Account created! Please check your email to verify your account.');
        }
      }

      setIsSigningUp(false);
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Signup error:', error);
      toast.error('An unexpected error occurred');
      setIsSigningUp(false);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Log audit event for logout
      if (user) {
        await supabase.rpc('log_audit_event', {
          p_event_type: 'user_logout',
          p_entity_type: 'auth',
          p_entity_id: user.id,
          p_details: null
        });
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Signed out successfully!');
        setProfile(null);
        setRoles([]);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password reset email sent!');
      }

      return { error };
    } catch (error) {
      toast.error('An unexpected error occurred');
      return { error };
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Log audit event
      await supabase.rpc('log_audit_event', {
        p_event_type: 'profile_update',
        p_entity_type: 'profile',
        p_entity_id: user.id,
        p_details: updates
      });

      // Invalidate cache and refresh
      cacheRef.current = null;
      await refreshProfile();
      toast.success('Profile updated successfully!');

      return { error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update profile';
      toast.error(message);
      return { error: error instanceof Error ? error : new Error(message) };
    }
  };

  const hasRole = (role: string) => {
    return roles.some(r =>
      r.role === role &&
      (!r.expires_at || new Date(r.expires_at) > new Date())
    );
  };

  const isAdmin = () => {
    return hasRole('admin') || hasRole('super_admin');
  };

  const isEnterpriseAdmin = () => {
    return hasRole('enterprise_admin') || hasRole('super_admin');
  };

  const isOrganizationAdmin = async (organizationId: string) => {
    if (!user) return false;

    const { data } = await supabase
      .from('organization_memberships')
      .select('role')
      .eq('user_id', user.id)
      .eq('organization_id', organizationId)
      .eq('role', 'admin')
      .maybeSingle();

    return !!data;
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    roles,
    loading,
    isSigningUp,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    hasRole,
    isAdmin,
    isEnterpriseAdmin,
    isOrganizationAdmin,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
